import {StyledTargetDeletionNameText} from '@/components/commons/delete-dialog/style'
import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {DialogHeading} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {formatLocaleDate} from '@/dayjs'
import {EarlyClosingProjectConfirmationFormValidationSchema} from '@/features/projects/formSchemas'
import {useStoreRenewTransformProject} from '@/features/projects/queries/useStoreRenewTransformProject'
import {ProjectExtended} from '@/features/projects/types'
import {assistiveRegimeToRehabCareCommitment} from '@/features/projects/utils'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {useTranslation} from '@/translations/i18n'
import {routes} from '@/utilities/constants/routes'
import {toast} from '@/utilities/toast/toast'
import {DialogStore} from '@ariakit/react'
import dayjs from 'dayjs'
import {FormProvider, useForm} from 'react-hook-form'
import {generatePath, useNavigate} from 'react-router-dom'
import {zodResolver} from '@hookform/resolvers/zod'

export const VariationProjectConfirmationModal = ({
    project,
    dialogStore,
    defaultValues
}: {
    project: ProjectExtended
    dialogStore: DialogStore
    defaultValues: EarlyClosingProjectConfirmationFormValidationSchema
}) => {
    const {t} = useTranslation()
    const form = useForm<EarlyClosingProjectConfirmationFormValidationSchema>({
        resolver: zodResolver(EarlyClosingProjectConfirmationFormValidationSchema),
        defaultValues
    })
    const navigate = useNavigate()

    const {mutate: editProjectDetails, isPending} = useStoreRenewTransformProject({
        projectId: project.id,
        options: {
            onSuccess: async data => {
                dialogStore.setOpen(false)
                void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECTS]})
                void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECT, project.id]})
                void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.AGENDA_EVENTS]})
                void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.USER_AVAILABILITY]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECTS]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT, project.id]})
                void queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.TREATMENT_REQUESTS, project.treatmentRequestId]
                })
                void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENTS, project.patientId]})
                toast.success(
                    t('projects:early_closing_reopening_project_success', {
                        closedProjectCode: project.code,
                        reopenedProjectCode: data.code
                    })
                )
                navigate(generatePath(routes.PROJECT_DETAILS_PROJECT_DATA.handle.path, {id: data.id}), {replace: true})
            },
            onError: () => {
                toast.error(t('errors:default'))
            }
        }
    })

    const handleConfirmSubmit = form.handleSubmit(
        formValues => {
            if (formValues.hasReopening == false || !project.type) {
                return
            }

            editProjectDetails({
                action: 'variation',
                empty: formValues.siarProject == 'empty',
                type: project.type,
                sector: formValues.sector,
                assistiveRegime: formValues.assistiveRegime || null,
                endDate: formatLocaleDate(formValues.endDate, 'YYYY-MM-DD'),
                startDate: formatLocaleDate(formValues.startDate, 'YYYY-MM-DD'),
                expirationDate: formatLocaleDate(formValues.expirationDate, 'YYYY-MM-DD'),
                durationDays: formValues.durationDays || dayjs(formValues.startDate).diff(formValues.endDate, 'day'),
                sender: formValues.sender,
                specialistId: formValues.specialist.id,
                // This is sent to the backend just to allow the SIAR export, it is not a field of our form
                rehabilitativeAssistanceCommitment:
                    formValues.sector == 'outpatient' && formValues.assistiveRegime
                        ? assistiveRegimeToRehabCareCommitment[formValues.assistiveRegime].toString()
                        : undefined,
                notificationRecipientIds: formValues.notificationsRecipients?.map(recipient => recipient.id),
                notificationDetails: formValues.notificationDetails
            })
        },
        error => {
            console.error(error)
        }
    )

    return (
        <FormProvider {...form}>
            <ConfirmationDialog
                dialogStore={dialogStore}
                onConfirm={handleConfirmSubmit}
                isConfirmPending={isPending}
                headerSlot={
                    <Flexbox direction="column">
                        <DialogHeading>{t('projects:project_early_closing_title')}</DialogHeading>
                        <Flexbox direction="column" gap={1}>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('projects:early_closing_project_confirmation')}
                            </Text>
                            <StyledTargetDeletionNameText palette="neutral" shade="500" size="textSm">
                                {t('projects:code_date', {
                                    code: project.code,
                                    date: dayjs(project.startDate).format('DD/MM/YYYY')
                                })}
                            </StyledTargetDeletionNameText>
                        </Flexbox>
                    </Flexbox>
                }
            />
        </FormProvider>
    )
}
